import React, { useState } from 'react';
import Papa from 'papaparse';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const REQUIRED_FIELDS = [
  'Trajectory ID', 'Algorithm', 'Model Type', 'Hurst Exponent', 'Diffusion Coefficient',
  'Anomalous Exponent', 'Trajectory Length', 'Mean Displacement', 'MSD Slope', 'Noise Level', 'Drift Detected'
];

const mockData = [
  { Algorithm: 'SPTNet', Hurst: 0.51, Alpha: 0.51, 'Diff Coeff': 0.0032, 'Noise Level': 0.09 },
  { Algorithm: 'BNPTrack', Hurst: 0.42, Alpha: 0.42, 'Diff Coeff': 0.0048, 'Noise Level': 0.12 },
  { Algorithm: 'TrackMate', Hurst: 0.63, Alpha: 0.63, 'Diff Coeff': 0.0021, 'Noise Level': 0.08 },
];

const trajectoryTable = [
  {
    id: 'SPTNet_1', Algorithm: 'SPTNet', Model: 'FBM', Hurst: 0.62,
    Alpha: 0.62, 'Diff Coeff': '2.3e-3', Length: 45, 'Mean Disp': 0.084,
    'MSD Slope': 0.6, 'Noise Level': 0.09, Drift: 'No'
  },
  {
    id: 'BNPTrack_1', Algorithm: 'BNPTrack', Model: 'CTRW', Hurst: 0.28,
    Alpha: 0.28, 'Diff Coeff': '1.8e-3', Length: 58, 'Mean Disp': 0.061,
    'MSD Slope': 0.3, 'Noise Level': 0.13, Drift: 'Yes'
  },
];

const UploadResultsPage = () => {
  const [fieldData, setFieldData] = useState({});
  const [data, setData] = useState([]);
  const [aggregated, setAggregated] = useState([]);
  const [missingFields, setMissingFields] = useState([]);

  const handleFieldUpload = (field, e) => {
    const file = e.target.files[0];
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (result) => {
        const values = result.data.map(r => r[0]);
        setFieldData(prev => ({ ...prev, [field]: values }));
      }
    });
  };

  const mergeFieldData = () => {
    const n = Math.max(...Object.values(fieldData).map(arr => arr.length));
    const rows = [];
    for (let i = 0; i < n; i++) {
      const row = {};
      REQUIRED_FIELDS.forEach(field => {
        row[field] = fieldData[field]?.[i] || '';
      });
      rows.push(row);
    }

    const missing = REQUIRED_FIELDS.filter(field => !fieldData[field]);
    setMissingFields(missing);
    setData(rows);

    if (missing.length === 0) {
      const grouped = {};
      rows.forEach(row => {
        const algo = row['Algorithm'];
        if (!grouped[algo]) grouped[algo] = { count: 0, hurst: 0, alpha: 0, diff: 0, noise: 0 };
        grouped[algo].count++;
        grouped[algo].hurst += parseFloat(row['Hurst Exponent'] || 0);
        grouped[algo].alpha += parseFloat(row['Anomalous Exponent'] || 0);
        grouped[algo].diff += parseFloat(row['Diffusion Coefficient'] || 0);
        grouped[algo].noise += parseFloat(row['Noise Level'] || 0);
      });

      const aggregatedData = Object.keys(grouped).map(algo => ({
        Algorithm: algo,
        'Hurst': +(grouped[algo].hurst / grouped[algo].count).toFixed(3),
        'Alpha': +(grouped[algo].alpha / grouped[algo].count).toFixed(3),
        'Diff Coeff': +(grouped[algo].diff / grouped[algo].count).toFixed(4),
        'Noise Level': +(grouped[algo].noise / grouped[algo].count).toFixed(3)
      }));

      setAggregated(aggregatedData);
    } else {
      setAggregated([]);
    }
  };

  return (
    <div className="p-6">

      <div className="bg-white shadow-md rounded-lg p-6 mb-10 border border-gray-200">
        <h2 className="text-2xl font-bold mb-4">📊 Upload Field-wise Trajectory Analysis Results</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {REQUIRED_FIELDS.map(field => (
            <div key={field} className="text-sm">
              <label className="block font-semibold mb-1">{field}</label>
              <input
                type="file"
                accept=".csv,.txt"
                onChange={(e) => handleFieldUpload(field, e)}
                className="text-sm"
              />
            </div>
          ))}
        </div>

        <button
          onClick={mergeFieldData}
          className="bg-blue-600 text-white px-4 py-2 rounded mb-4 hover:bg-blue-700"
        >
          📥 Merge and Analyze
        </button>

        {missingFields.length > 0 && (
          <p className="text-red-600 mt-2 text-sm">❌ Missing fields: {missingFields.join(', ')}</p>
        )}
      </div>

      {aggregated.length > 0 && (
        <div className="bg-gray-50 shadow-inner rounded-lg p-6 mb-10 border border-blue-100">
          <h3 className="text-xl font-semibold mb-4 text-blue-800 border-b border-blue-200 pb-1">
            🔍 Aggregated Metrics by Algorithm
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={aggregated} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Algorithm" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Hurst" fill="#8884d8" />
              <Bar dataKey="Alpha" fill="#82ca9d" />
              <Bar dataKey="Diff Coeff" fill="#ffc658" />
              <Bar dataKey="Noise Level" fill="#ff7f7f" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {data.length > 0 && (
        <div className="bg-white shadow-md rounded-lg p-6 mb-10 border border-gray-200 overflow-x-auto">
          <h3 className="text-xl font-semibold mb-4 text-blue-800 border-b border-blue-200 pb-1">
            📋 Detailed Trajectory Results
          </h3>
          <table className="min-w-full text-sm text-left border">
            <thead className="bg-gray-100">
              <tr>
                {REQUIRED_FIELDS.map((key) => (
                  <th key={key} className="px-4 py-2 border">{key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={idx} className="odd:bg-white even:bg-gray-50">
                  {REQUIRED_FIELDS.map((key) => (
                    <td key={key} className="px-4 py-2 border">{row[key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-slate-50 rounded-lg p-6 mt-10 border-t-2 border-dashed border-gray-300">
        <h3 className="text-xl font-semibold mb-4 text-blue-800 border-b border-blue-200 pb-1">📉 Sample Chart: Model Type Distribution (Mock)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={mockData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="Algorithm" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="Hurst" fill="#8884d8" />
            <Bar dataKey="Alpha" fill="#82ca9d" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-slate-50 rounded-lg p-6 mt-10 border-t-2 border-dashed border-gray-300 overflow-x-auto">
        <h3 className="text-xl font-semibold mb-4 text-blue-800 border-b border-blue-200 pb-1">📋 Static Example Table</h3>
        <table className="min-w-full text-sm text-left border">
          <thead className="bg-gray-100">
            <tr>
              {Object.keys(trajectoryTable[0]).map((key) => (
                <th key={key} className="px-4 py-2 border">{key}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trajectoryTable.map((row, idx) => (
              <tr key={idx} className="odd:bg-white even:bg-gray-50">
                {Object.values(row).map((val, i) => (
                  <td key={i} className="px-4 py-2 border">{val}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UploadResultsPage;
